import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";

type Option = {
  label: string;
  value: string;
};
type SelectProps = {
  onOptionSelected?: (option: Option | null) => void;
  options: Option[];
  label: string;
  sx?: SxProps;
  multiple?: boolean;
  enablePortal?: boolean;
  required?: boolean;
  isLoading?: boolean;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export type RhfSelect<T extends FieldValues> = SelectProps &
  UseControllerProps<T>;

export default function Select<T extends FieldValues>({
  control,
  name,
  onOptionSelected,
  options,
  required,
  label,
  isLoading,
  multiple,
  enablePortal,
  sx
}: RhfSelect<T>) {
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: { label: string; value: string }) => option.label
  });

  return (
    <AutocompleteFormField
      openOnFocus
      autoHighlight
      autoFocus
      enablePortal={enablePortal}
      name={name}
      multiple={multiple}
      label={label}
      options={options}
      isLoading={isLoading}
      required={required}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option?.label ?? ""}
      renderOption={(props: object, option: any, other: any) => (
        <ListItem {...props}>
          {multiple && (
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={other?.selected}
            />
          )}
          <ListItemText>{option?.label}</ListItemText>
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => {
        return option?.value === value?.value;
      }}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onOptionSelected={(option: any) => {
        if (onOptionSelected) {
          onOptionSelected(option);
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 160, ...sx }}
    />
  );
}
